import { memo, useState } from "react";

import { Errors } from "services/forms/validation/types";

// Tipovi i konstante
import { VehicleSearchFiltersModalProps } from "./types";
import { CONDITION_OPTIONS, FUEL_OPTIONS } from "./consts";

// UI komponente
import { Button } from "ui/components/Button/Button";

import { Loader } from "ui/components/Loader";
import { useTranslate } from "helpers";

import { Dropdown } from "ui/components/Dropdown/Dropdown";
import OfficeDropdown from "ui/components/Dropdown/OfficeDropdown/OfficeDropdown";
import VehicleCategoryDropdown from "ui/components/Dropdown/VehicleCategoryDropdown/VehicleCategoryDropdown";
import VehicleManufacturerDropdown from "ui/components/Dropdown/VehicleManufacturerDropdown/VehicleManufacturerDropdown";
import VehicleModelDropdown from "ui/components/Dropdown/VehicleModelDropdown/VehicleModelDropdown";
import VehicleTypeDropdown from "ui/components/Dropdown/VehicleTypeDropdown/VehicleTypeDropdown";

import {
  useHandleDateChange,
  useHandleDropdownChange,
  useHandleTextFieldChange,
} from "services/forms/hooks";
import { TextField } from "ui/components/TextField/TextField";
import { DatePicker } from "ui/components/DatePicker";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";

const VehicleSearchFiltersModal: React.FC<VehicleSearchFiltersModalProps> = (
  props
) => {
  const { show, onSuccess, onClose, formValues, setFormValues } = props;

  const { t } = useTranslate();

  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleDropdownChange = useHandleDropdownChange(
    setFormValues,
    formValues,
    formValues.advancedSearch
  );

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues,
    formValues.advancedSearch
  );

  const dateFilterLabels: Record<string, string> = {
    firstRegistrationDateFrom: t(
      "admin.fleet.vehicle.search.firstregistrationdatefrom"
    ),
    firstRegistrationDateTo: t(
      "admin.fleet.vehicle.search.firstregistrationdateto"
    ),
    registrationExpirationFrom: t(
      "admin.fleet.vehicle.search.registrationexpirationfrom"
    ),
    registrationExpirationTo: t(
      "admin.fleet.vehicle.search.registrationexpirationto"
    ),
  };

  const handleDateChange = useHandleDateChange(
    setFormValues,
    formValues,
    formValues.advancedSearch,
    (name) => dateFilterLabels[name]
  );

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    setIsLoading(true);

    try {
      onSuccess(formValues);
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  return (
    <Modal show={show} onHide={onClose} size="small">
      <ModalHeader
        icon="/media/images/search-icon.svg"
        title={t("admin.fleet.vehicle.search.filter.title")}
        onHide={onClose}
      />
      <ModalBody>
      <div className="flex gap-4">
        <div className="w-full m-auto">
          <div className="space-y-3"></div>
          <form noValidate className="text-left">
            <div className="px-5 pt-4 pb-3 mb-2">
              <div>
                <label className="block text-sm mb-2 font-medium leading-6 text-[#344054]">
                  Vozilo
                </label>
                <div className="flex flex-col gap-3 mb-3">
                  <div className="flex-1 ">
                    <VehicleCategoryDropdown
                      placeholder={t("admin.vehicle.category.placeholder")}
                      name="vehicleCategoryId"
                      value={formValues.vehicleCategoryId}
                      onChange={handleDropdownChange}
                      error={errors.vehicleCategoryId}
                    />
                  </div>
                  <div className="flex-1 ">
                    <VehicleManufacturerDropdown
                      placeholder={t("admin.vehicle.manufacturer.placeholder")}
                      name="vehicleManufacturerId"
                      vehicleCategoryId={formValues.vehicleCategoryId}
                      value={formValues.vehicleManufacturerId}
                      onChange={handleDropdownChange}
                      disabled={!formValues.vehicleCategoryId}
                      error={errors.vehicleManufacturerId}
                      required={true}
                    />
                  </div>
                  <div className="flex-1 ">
                    <VehicleModelDropdown
                      vehicleManufacturerId={formValues.vehicleManufacturerId}
                      placeholder={t("admin.vehicle.model.placeholder")}
                      name="vehicleModelId"
                      value={formValues.vehicleModelId}
                      onChange={handleDropdownChange}
                      error={errors.vehicleModelId}
                      disabled={!formValues.vehicleManufacturerId}
                      required={true}
                    />
                  </div>
                  <div className="flex-1 ">
                    <VehicleTypeDropdown
                      vehicleManufacturerId={formValues.vehicleManufacturerId}
                      vehicleModelId={formValues.vehicleModelId}
                      placeholder={t("admin.vehicle.type.placeholder")}
                      name="vehicleTypeId"
                      value={formValues.vehicleTypeId}
                      onChange={handleDropdownChange}
                      error={errors.vehicleTypeId}
                      disabled={!formValues.vehicleModelId}
                      required={true}
                    />
                  </div>
                  {/* Stanje, gorivo i poslovnica su prije bili u traci filtera; ovdje su
                      jer se rijetko mijenjaju, a traku su razvlačili. */}
                  <div className="flex-1">
                    <Dropdown
                      name="condition"
                      label="Stanje"
                      placeholder="Odaberite stanje"
                      options={CONDITION_OPTIONS}
                      value={formValues.condition}
                      onChange={handleDropdownChange}
                      error={errors.condition}
                    />
                  </div>
                  <div className="flex-1">
                    <Dropdown
                      name="fuelType"
                      label="Gorivo"
                      placeholder="Odaberite gorivo"
                      options={FUEL_OPTIONS}
                      value={formValues.fuelType}
                      onChange={handleDropdownChange}
                      error={errors.fuelType}
                    />
                  </div>
                  <div className="flex-1">
                    <OfficeDropdown
                      name="officeId"
                      label="Poslovnica"
                      placeholder="Odaberite poslovnicu"
                      value={formValues.officeId}
                      onChange={handleDropdownChange}
                      error={errors.officeId}
                    />
                  </div>
                  <div className="flex-1">
                    <TextField
                      type="text"
                      name="vinNumber"
                      label={t("admin.fleet.vehicle.search.vin")}
                      placeholder={t(
                        "admin.fleet.vehicle.search.vin.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      error={errors.vinNumber}
                      value={formValues.vinNumber}
                    
                    ></TextField>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <DatePicker
                        name="firstRegistrationDateFrom"
                        label={t(
                          "admin.fleet.vehicle.search.firstregistrationdatefrom"
                        )}
                        placeholder={t(
                          "admin.fleet.vehicle.search.firstregistrationdatefrom.placeholder"
                        )}
                        onChange={handleDateChange}
                        error={errors.firstRegistrationDateFrom}
                        value={formValues.firstRegistrationDateFrom}
                      
                      />
                    </div>
                    <div className="flex-1 mt-8">
                      <DatePicker
                        name="firstRegistrationDateTo"
                        placeholder={t(
                          "admin.fleet.vehicle.search.firstregistrationdateto.placeholder"
                        )}
                        onChange={handleDateChange}
                        error={errors.firstRegistrationDateTo}
                        value={formValues.firstRegistrationDateTo}
                        width="full"
                      
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <TextField
                      type="text"
                      name="registrationPlate"
                      label={t("admin.fleet.vehicle.search.registrationplate")}
                      placeholder={t(
                        "admin.fleet.vehicle.search.registrationplate.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      error={errors.registrationPlate}
                      value={formValues.registrationPlate}
                    
                    ></TextField>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <DatePicker
                        name="registrationExpirationFrom"
                        label={t(
                          "admin.fleet.vehicle.search.registrationexpirationfrom"
                        )}
                        placeholder={t(
                          "admin.fleet.vehicle.search.registrationexpirationfrom.placeholder"
                        )}
                        onChange={handleDateChange}
                        error={errors.registrationExpirationFrom}
                        value={formValues.registrationExpirationFrom}
                      
                      />
                    </div>
                    <div className="flex-1 mt-8">
                      <DatePicker
                        name="registrationExpirationTo"
                        placeholder={t(
                          "admin.fleet.vehicle.search.registrationexpirationto.placeholder"
                        )}
                        onChange={handleDateChange}
                        error={errors.registrationExpirationTo}
                        value={formValues.registrationExpirationTo}
                        width="full"
                      
                      />
                    </div>
                  </div>

                  <div className="flex flex-row gap-3 ">
                    <div className="flex-1 ">
                      <TextField
                        type="number"
                        name="kwFrom"
                        label={t("admin.fleet.vehicle.search.kwfrom")}
                        placeholder={t(
                          "admin.fleet.vehicle.search.kwfrom.placeholder"
                        )}
                        onChange={handleTextFieldChange}
                        error={errors.kwFrom}
                        value={formValues.kwFrom}
                        groupAppend="kw"

                      ></TextField>
                    </div>
                    <div className="flex-1 mt-8">
                      <TextField
                        type="number"
                        name="kwTo"
                        placeholder={t(
                          "admin.fleet.vehicle.search.kwto.placeholder"
                        )}
                        onChange={handleTextFieldChange}
                        error={errors.kwTo}
                        value={formValues.kwTo}
                        groupAppend="kw"

                      ></TextField>
                    </div>
                  </div>
                  <div className="flex flex-row gap-3 ">
                    <div className="flex-1">
                      <TextField
                        type="number"
                        name="kmTraveledFrom"
                        label={t("admin.fleet.vehicle.search.kmtraveledfrom")}
                        placeholder={t(
                          "admin.fleet.vehicle.search.kmtraveledfrom.placeholder"
                        )}
                        onChange={handleTextFieldChange}
                        error={errors.kmTraveledFrom}
                        value={formValues.kmTraveledFrom}
                        groupAppend="km"
                      
                      ></TextField>
                    </div>
                    <div className="flex-1 mt-8">
                      <TextField
                        type="number"
                        name="kmTraveledTo"
                        placeholder={t(
                          "admin.fleet.vehicle.search.kmtraveledto.placeholder"
                        )}
                        onChange={handleTextFieldChange}
                        error={errors.kmTraveledTo}
                        value={formValues.kmTraveledTo}
                        groupAppend="km"
                      
                      ></TextField>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
              <Button
                variant="outlined"
                children={t("admin.close")}
                onClick={() => onClose()}
              />
              <Button
                variant="primary"
                children={t("admin.search")}
                onClick={handleFormSubmit}
              />
            </div>
          </form>
        </div>
      </div>

      <Loader isLoading={isLoading ?? false}> </Loader>
      </ModalBody>
    </Modal>
  );
};
export default memo(VehicleSearchFiltersModal);
